// Helper functions ===================================================================================

// Randomly select element from array
function randomlySelect(arrayInput){
    return arrayInput[Math.floor(arrayInput.length*Math.random())]; 
}

// Capitalize first letter (when changing wording)
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

// Reduce fractions, see: https://stackoverflow.com/questions/4652468/is-there-a-javascript-function-that-reduces-a-fraction
function reduce(numerator,denominator){
    var gcd = function gcd(a,b){
        return b ? gcd(b, a%b) : a;
    };
    gcd = gcd(numerator,denominator);
    return [numerator/gcd, denominator/gcd];
}

function getTriangle3rdSide(side1,side2){
    let side3Max = side1 + side2; // exclusive...
    let side3Min = Math.max(side1, side2) - Math.min(side1, side2) + 1; // exclusive...
    let side3 = Math.floor((side3Max - side3Min)*Math.random()) + side3Min;
    return side3;
}

// Problem generation functions ==========================================================================

export const shapes = {

    square: {
        findPerimeter: {
            numbers: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1; // range of side lengths divisible by 4
                let hardNumber = Math.floor(49*Math.random())+1;
                let parameter = (difficulty>1) ? hardNumber : easyNumber;
                // Vary the specific wording
                let wordChoiceLength = ["a side length of", "a length of", "length", "a width of", "width", "a width measurement of", "one side of length", "one side length of"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a square with ${wordingLength} ${parameter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has ${wordingLength} ${parameter}${wordingUnit}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a square has ${wordingLength} ${parameter}${wordingUnit}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land is ${parameter}${wordingUnit} wide and is surrounded by a fence along all its sides. What is the total length of this fence?` :
                            `What is the total length of a fence that goes completely around a square plot of land that has ${wordingLength} ${parameter}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting is ${parameter}${wordingUnit} wide.  It needs a wooden frame around its entire border. What is the total length of wood needed to frame the painting?` :
                            `You're crafting art with a square piece of paper.  The paper has ${wordingLength} ${parameter}${wordingUnit}. You want to attach a string all along the edge of the paper around the whole square.  How long must the string be?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a square with ${wordingLength} ${parameter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = parameter*4;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let squared = Math.pow(parameter,2);
                if(squared === correctAnswerNumber){
                    squared = correctAnswerNumber/2;
                } 
                let wrong1Number = squared;
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let halfSquared = (squared%2===0) ? squared/2 : `${squared}&frasl;2`;
                if(halfSquared===correctAnswerNumber || halfSquared===wrong1Number){
                    halfSquared = correctAnswerNumber + 2;
                }
                let wrong2Number = halfSquared;
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let fraction = reduce(parameter,4);
                let otherErrors = (parameter%4===0) ? parameter/4 : (parameter%3===0) ? parameter/3 : `${fraction[0]}&frasl;${fraction[1]}`;
                if(otherErrors===correctAnswerNumber || otherErrors===wrong1Number || otherErrors===wrong2Number){
                    otherErrors = correctAnswerNumber + 3;
                } 
                let wrong3Text = `${otherErrors}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // Get parameters
                let side = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(49*Math.random())+1;
                let area = Math.pow(side,2);
                let perimeter = 4*side; 
                let parameter = area; // parameter to show in text
                // Vary the specific wording
                let wordChoiceA = ["an area of", "area"];
                let wordingA = randomlySelect(wordChoiceA);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a square with ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a square has ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land covers ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. It has a fence surrounding it on all sides.  What is the total length of this fence?` :
                            `What is the total length of a fence that goes completely around a square plot of land that has ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting has an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.  It needs a wooden frame around its entire border. What is the total length of wood needed to frame the painting?` :
                            `You're crafting art with a square piece of paper.  The paper has ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. You want to attach a string all along the edge of the paper around the whole square.  How long must the string be?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a square with ${wordingA} ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = perimeter;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = side;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = correctAnswerNumber - Math.floor(10*Math.random()+1);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = area/4;
                if(area%4!==0){
                    let fraction = reduce(area,4);
                    wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong2Number || wrong3Number===wrong1Number ){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        },
        findArea: {
            numbers: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1; // range of side lengths divisible by 4
                let hardNumber = Math.floor(19*Math.random())+1;
                let parameter = (difficulty>1) ? hardNumber : easyNumber;
                // Vary the specific wording
                let wordChoiceLength = ["a side length of", "a length of", "length", "a width of", "width", "a width measurement of", "one side of length", "one side length of"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a square with ${wordingLength} ${parameter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has ${wordingLength} ${parameter}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a square has ${wordingLength} ${parameter}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land is ${parameter}${wordingUnit} wide. What is its area?` :
                            `What is the area of a square plot of land that has ${wordingLength} ${parameter}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting is ${parameter}${wordingUnit} wide. How much area on the wall does this painting cover?` :
                            `You're crafting art with a square piece of paper.  The paper has ${wordingLength} ${parameter}${wordingUnit}. What is the area of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a square with ${wordingLength} ${parameter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = Math.pow(parameter,2);
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = 4*parameter;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = 2*parameter;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = Math.pow(parameter,2)/2;
                if(Math.pow(parameter,2)%2!==0){
                    wrong3Number = `${Math.pow(parameter,2)}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenPerimeter: function(difficulty) {
                // Get parameters
                let side = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(49*Math.random())+1;
                let area = Math.pow(side,2);
                let perimeter = 4*side; 
                let parameter = perimeter; // parameter to show in text
                // Vary the specific wording
                let wordChoiceP = ["a perimeter of", "perimeter"];
                let wordingP = randomlySelect(wordChoiceP);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a square with ${wordingP} ${parameter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has ${wordingP} ${parameter}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a square has ${wordingP} ${parameter}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land has ${parameter}${wordingUnit} of fencing surrounding it. What is its area?` :
                            `What is the area of a square plot of land that has ${wordingP} ${parameter}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting has a wooden frame around its border, and this frame uses ${parameter}${wordingUnit} of wood total. How much area on the wall does this painting cover?` :
                            `You're crafting art with a square piece of paper.  The paper has ${wordingP} ${parameter}${wordingUnit}. What is the area of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a square with ${wordingP} ${parameter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = area;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = side;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = correctAnswerNumber - Math.floor(10*Math.random()+1);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = Math.pow(perimeter,2)/2;
                if(Math.pow(perimeter,2)%2!==0){
                    wrong3Number = `${Math.pow(perimeter,2)}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        },
        findMissingDimension: {
            givenPerimeter: function(difficulty) {
                // Get given perimeter
                let easyP = 4*Math.floor(11*Math.random() + 1); // range of perimeters divisible by 4
                let hardP = Math.floor(49*Math.random())+1;
                let parameter = (difficulty>1) ? hardP : easyP;
                // Vary the specific wording
                let wordChoiceSide = ["its width", "its length", "the length of one of its sides", "how long it is on one side", "how wide it is", "its length on one side"]; // what is...
                let wordingSide= randomlySelect(wordChoiceSide);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the width of a square with a perimeter of ${parameter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has a perimeter of ${parameter}${wordingUnit}, then what is its width?`
                        break;
                    case 3:
                        problemDataText = `If a square has a perimeter of ${parameter}${wordingUnit}, then ${wordingCommand} ${wordingSide}.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land has ${parameter}${wordingUnit} of fencing surrounding it along all its sides. What is the the length of one of the sides of this plot of land?` :
                            `How wide is a square plot of land that has a perimeter of ${parameter}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting has a wooden frame around its entire border.  This frame uses ${parameter}${wordingUnit} of wood.  What is the length of one side of the painting?` :
                            `You're crafting art with a square piece of paper.  The paper has a perimeter of ${parameter}${wordingUnit}. How long is one side of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the side length of a square whose perimeter is ${parameter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = parameter/4;
                if(parameter%4!==0){
                    let fraction = reduce(parameter,4);
                    correctAnswerNumber = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = parameter*4;
                if(wrong1Number === correctAnswerNumber){
                    wrong1Number = Math.round(correctAnswerNumber) + 1;
                } 
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = parameter/2;
                if(parameter%2!==0){
                    wrong2Number = `${parameter}&frasl;2`;
                }
                if(wrong2Number === correctAnswerNumber || wrong2Number === wrong1Number){
                    wrong2Number = Math.round(correctAnswerNumber) + 2;
                } 
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = Math.pow(parameter,2)/2;
                if(Math.pow(parameter,2)%2!==0){
                    wrong3Number = `${Math.pow(parameter,2)}&frasl;2`;
                }
                if(wrong3Number === correctAnswerNumber || wrong3Number === wrong1Number || wrong3Number === wrong2Number){
                    wrong3Number = Math.round(parameter/4) + 3;
                } 
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // Get correct answer number (nice numbers)
                let correctAnswerNumber = (difficulty<2) ? Math.floor(14*Math.random()) + 1 : Math.floor(19*Math.random()) + 1; // side length
                // Get given area
                let parameter = Math.pow(correctAnswerNumber,2); // parameter = area
                // Vary the specific wording
                let wordChoiceSide = ["its width", "its length", "the length of one of its sides", "how long it is on one side", "how wide it is", "its length on one side"]; // what is...
                let wordingSide= randomlySelect(wordChoiceSide);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the width of a square with an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a square has an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its width?`
                        break;
                    case 3:
                        problemDataText = `If a square has an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} ${wordingSide}.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square plot of land covers and area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the the length of one of the sides of this plot of land?` :
                            `How wide is a square plot of land that has an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A square-shaped painting covers ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} of a wall.  What is the length of one side of the painting?` :
                            `You're crafting art with a square piece of paper.  The paper has an area of ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. How long is one side of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the side length of a square whose area is ${parameter}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = parameter/4;
                if(parameter%4!==0){
                    let fraction = reduce(parameter,4);
                    wrong1Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong1Number === correctAnswerNumber){
                    wrong1Number = Math.round(correctAnswerNumber) + 1;
                } 
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = parameter/2;
                if(parameter%2!==0){
                    wrong2Number = `${parameter}&frasl;2`;
                }
                if(wrong2Number === correctAnswerNumber || wrong2Number === wrong1Number){
                    wrong2Number = Math.round(correctAnswerNumber) + 2;
                } 
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = Math.pow(parameter,2);
                if(wrong3Number === correctAnswerNumber || wrong3Number === wrong1Number || wrong3Number === wrong2Number){
                    wrong3Number = Math.round(correctAnswerNumber) + 4;
                } 
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        }
    },

    rectangle: {
        findPerimeter: {
            numbers: function(difficulty) {
                // Get parameters
                let easyL = Math.floor(11*Math.random())+1; 
                let hardL = Math.floor(19*Math.random())+1;
                let paramL = (difficulty>1) ? hardL : easyL;
                let easyW = Math.floor(11*Math.random())+1;
                let hardW = Math.floor(19*Math.random())+1;
                let paramW = (difficulty>1) ? hardW : easyW;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a rectangle with ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular plot of land is ${paramW}${wordingUnit} wide and ${paramL}${wordingUnit} long, and it is surrounded by a fence along all its sides. What is the total length of this fence?` :
                            `What is the perimeter of a rectangular plot of land that has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular painting is ${paramW}${wordingUnit} wide and ${paramL}${wordingUnit} tall. It needs a wooden frame around its entire border. What is the total length of wood needed to frame the painting?` :
                            `You're crafting art with a rectangular piece of paper that is ${paramL}${wordingUnit} by ${paramW}${wordingUnit}. You want to attach a string all along the edge of the paper around the whole shape.  How long must the string be?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a rectangle with ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = 2*paramL + 2*paramW;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = paramL*paramW;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = paramL + paramW;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = paramL*paramW/2;
                if((paramL*paramW)%2!==0){
                    wrong3Number = `${paramL*paramW}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // Get parameters
                let param1 = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(19*Math.random())+1;
                let param2 = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(19*Math.random())+1;
                let perimeter = 2*param1 + 2*param2;
                let area = param1*param2;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a rectangle with ${wordingLength} ${param1}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingWidth} ${param1}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingLength} ${param1}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular plot of land is ${param1}${wordingUnit} wide and has an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. It is surrounded by a fence along all its sides. What is the total length of this fence around the whole plot of land?` :
                            `What is the perimeter of a rectangular plot of land that has ${wordingLength} ${param1}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular painting is ${param1}${wordingUnit} wide and has an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. It needs a wooden frame around its entire border. What is the total length of wood needed to make the frame around the painting?` :
                            `You're crafting art with a rectangular piece of paper that is ${param1}${wordingUnit} long and has an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. You want to attach a string all along the edge of the paper around the whole shape.  How long must the string be?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a rectangle with ${wordingLength} ${param1}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = perimeter;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = param1*area;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = param2;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = 2*param1 + 2*area;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }    
        },
        findArea: {
            numbers: function(difficulty) {
                // Get parameters
                let easyL = Math.floor(11*Math.random())+1; 
                let hardL = Math.floor(19*Math.random())+1;
                let paramL = (difficulty>1) ? hardL : easyL;
                let easyW = Math.floor(11*Math.random())+1;
                let hardW = Math.floor(19*Math.random())+1;
                let paramW = (difficulty>1) ? hardW : easyW;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a rectangle with ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `A rectangular plot of land is ${paramW}${wordingUnit} wide and ${paramL}${wordingUnit} long. What is its area?` :
                        `What is the area of a rectangular plot of land that has ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular painting is ${paramW}${wordingUnit} wide and ${paramL}${wordingUnit} tall. How much area on the wall does this painting cover?` :
                            `You're crafting art with a rectangular piece of paper.  The paper is ${paramL}${wordingUnit} by ${paramW}${wordingUnit}. What is the area of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a rectangle with ${wordingLength} ${paramL}${wordingUnit} and ${wordingWidth} ${paramW}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = paramL*paramW;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = 2*paramL + 2*paramW;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = paramL + paramW;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = paramL*paramW/2;
                if((paramL*paramW)%2!==0){
                    wrong3Number = `${paramL*paramW}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenPerimeter: function(difficulty) {
                // Get parameters
                let param1 = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(19*Math.random())+1;
                let param2 = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(19*Math.random())+1;
                let perimeter = 2*param1 + 2*param2;
                let area = param1*param2;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a rectangle with ${wordingLength} ${param1}${wordingUnit} and a perimeter of ${perimeter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingWidth} ${param1}${wordingUnit} and a perimeter of ${perimeter}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingLength} ${param1}${wordingUnit} and a perimeter of ${perimeter}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `A rectangular plot of land is ${param1}${wordingUnit} wide and has ${perimeter}${wordingUnit} of fencing going all around it on all sides. What is its area?` :
                        `What is the area of a rectangular plot of land that has ${wordingLength} ${param1}${wordingUnit} and a perimeter of ${perimeter}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular painting is ${param1}${wordingUnit} wide and has a perimeter of ${perimeter}${wordingUnit}. How much area on the wall does this painting cover?` :
                            `You're crafting art with a rectangular piece of paper.  The paper is ${param1}${wordingUnit} long and has a perimeter of ${perimeter}${wordingUnit}. What is the area of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a rectangle with ${wordingLength} ${param1}${wordingUnit} and a perimeter of ${perimeter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = area;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = param2;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = param1*perimeter;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = param2 + param1;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        },
        findMissingDimension: {
            givenPerimeter: function(difficulty) {
                // easy mode....
                // Get correct missing dimension, length or width
                let correctAnswerNumber = Math.floor(11*Math.random()) + 1;
                // Get given perimeter
                let otherDimension = (difficulty<2) ? Math.floor(11*Math.random()) + 1 
                    : Math.floor(19*Math.random()) + 1;
                let perimeter = 2*correctAnswerNumber + 2*otherDimension;
                // hard mode....
                if(difficulty>1){
                    perimeter = 2*otherDimension + Math.floor(49*Math.random()) + 1;
                    correctAnswerNumber = (perimeter - 2*otherDimension)/2;
                    if((perimeter - 2*otherDimension)%2!==0){
                        let fraction = reduce((perimeter - 2*otherDimension),2);
                        correctAnswerNumber = `${fraction[0]}&frasl;${fraction[1]}`;
                    }
                }
                // Vary the specific wording
                let wordChoiceP = ["a perimeter of", "perimeter"]; 
                let wordingP = randomlySelect(wordChoiceP);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the width of a rectangle with ${wordingP} ${perimeter}${wordingUnit} and length ${otherDimension}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingP} ${perimeter}${wordingUnit} and width ${otherDimension}${wordingUnit}, then what is its length?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingP} ${perimeter}${wordingUnit} and length ${otherDimension}${wordingUnit}, then ${wordingCommand} its width.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular plot of land has ${perimeter}${wordingUnit} of fencing surrounding it along all its sides. The plot of land is ${otherDimension}${wordingUnit} wide.  How long is it?` :
                            `How wide is a rectangular plot of land that has ${wordingP} ${perimeter}${wordingUnit} and a length of ${otherDimension}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular-shaped painting has a wooden frame around its entire border.  This frame uses ${perimeter}${wordingUnit} of wood.  What is the length of the painting, if it is ${otherDimension}${wordingUnit} wide?` :
                            `You're crafting art with a rectangular piece of paper.  The paper has a perimeter of ${perimeter}${wordingUnit} and a length of ${otherDimension}${wordingUnit}. What is the width of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the length of a rectangle whose perimeter is ${perimeter}${wordingUnit} and whose width is ${otherDimension}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = perimeter - 2*otherDimension;
                if(wrong1Number === correctAnswerNumber){
                    wrong1Number = Math.round(correctAnswerNumber) + 1;
                } 
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = 2*perimeter + 2*otherDimension;
                if(wrong2Number === correctAnswerNumber || wrong2Number === wrong1Number){
                    wrong2Number = Math.round(correctAnswerNumber) + 2;
                } 
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = perimeter/otherDimension;
                if(perimeter%otherDimension!==0){
                    let fraction = reduce(perimeter,otherDimension);
                    wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong3Number === correctAnswerNumber || wrong3Number === wrong1Number || wrong3Number === wrong2Number){
                    wrong3Number = Math.round(correctAnswerNumber) + 3;
                } 
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // easy mode....
                // Get correct missing dimension, length or width
                let correctAnswerNumber = (difficulty<2) ? Math.floor(11*Math.random()) + 1 
                    : Math.floor(19*Math.random()) + 1;
                // Get given area
                let otherDimension = (difficulty<2) ? Math.floor(11*Math.random()) + 1 
                    : Math.floor(19*Math.random()) + 1;
                let area = correctAnswerNumber*otherDimension;
                // Vary the specific wording
                let wordChoiceA = ["an area of", "area"]; 
                let wordingA = randomlySelect(wordChoiceA);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the width of a rectangle with ${wordingA} ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and length ${otherDimension}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangle has ${wordingA} ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and width ${otherDimension}${wordingUnit}, then what is its length?`
                        break;
                    case 3:
                        problemDataText = `If a rectangle has ${wordingA} ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and length ${otherDimension}${wordingUnit}, then ${wordingCommand} its width.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular plot of land covers ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. The plot of land is ${otherDimension}${wordingUnit} wide.  How long is it?` :
                            `How wide is a rectangular plot of land that has ${wordingA} ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and a length of ${otherDimension}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A rectangular-shaped painting covers ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} of the wall.  What is the length of the painting, if it is ${otherDimension}${wordingUnit} wide?` :
                            `You're crafting art with a rectangular piece of paper.  The paper has an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and a length of ${otherDimension}${wordingUnit}. What is the width of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the length of a rectangle whose area is ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""} and whose width is ${otherDimension}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = area - 2*otherDimension;
                if(wrong1Number === correctAnswerNumber){
                    wrong1Number = Math.round(correctAnswerNumber) + 1;
                } 
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = area*otherDimension;
                if(wrong2Number === correctAnswerNumber || wrong2Number === wrong1Number){
                    wrong2Number = Math.round(correctAnswerNumber) + 2;
                } 
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = correctAnswerNumber/2;
                if(correctAnswerNumber%2!==0){
                    let fraction = reduce(correctAnswerNumber,2);
                    wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong3Number === correctAnswerNumber || wrong3Number === wrong1Number || wrong3Number === wrong2Number){
                    wrong3Number = Math.round(correctAnswerNumber) + 3;
                } 
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        }
    },

    triangle: {
        findPerimeter: {
            numbers: function(difficulty) {
                // Get parameters
                let easy1 = Math.floor(11*Math.random())+1; 
                let hard1 = Math.floor(15*Math.random())+1;
                let param1 = (difficulty>1) ? hard1 : easy1;
                let easy2 = Math.floor(11*Math.random())+1;
                let hard2 = Math.floor(15*Math.random())+1;
                let param2 = (difficulty>1) ? hard2 : easy2;
                let easy3 = getTriangle3rdSide(easy1,easy2);
                let hard3 = getTriangle3rdSide(hard1,hard2);
                let param3 = (difficulty>1) ? hard3 : easy3;
                // Vary the specific wording
                let wordChoiceLength = ["with side lengths of", "with sides that are", "whose sides are", "whose side lengths are"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a triangle ${wordingLength} ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a triangle has sides of ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a triangle has sides of ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular plot of land has sides of ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}. It is surrounded by a fence bordering all its sides. What is the total length of this fence?` :
                            `What is the perimeter of a triangular plot of land ${wordingLength} ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular kite has side lengths of ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}. It has a metal frame along its outer edge on all three sides. What is the total length of the metal frame around the kite?` :
                            `You're crafting art with a triangular piece of paper that has side lengths of ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}. You want to attach a string all along the edge of the paper around the whole shape.  How long must the string be?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a triangle ${wordingLength} ${param1}${wordingUnit}, ${param2}${wordingUnit}, and ${param3}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = param1 + param2 + param3;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = param1*param2*param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = param1 + param2 + param3 + Math.floor(10*Math.random()) - 5;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = param1*param2*param3/2;
                if((param1*param2*param3)%2!==0){
                    wrong3Number = `${param1*param2*param3}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            ofRightTriangle: function(difficulty){
                // Get parameters; only perfect Pythag triples
                let pythagTriples = [[3,4,5],[5,12,13],[6,8,10]];
                if(difficulty>1){
                    pythagTriples.push([9,12,15],[10,24,26],[11,60,61],[12,16,20],[15,20,25]);
                }
                let sides = randomlySelect(pythagTriples);
                let legs = (Math.random()>0.5) ? [sides[0],sides[1]] : [sides[1],sides[0]];
                let param1 = legs[0];
                let param2 = legs[1];
                let param3 = sides[2]; // hypotenuse
                let perimeter = param1 + param2 + param3;
                // Vary the specific wording
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                let correctAnswerNumber = 0;
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the perimeter of a right triangle with one leg of length ${param1}${wordingUnit} and another leg of length ${param2}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a right triangle has one leg of length ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}, then what is its perimeter?`
                        break;
                    case 3:
                        problemDataText = `If a right triangle has legs of length ${param1}${wordingUnit} and ${param2}${wordingUnit}, then ${wordingCommand} its perimeter.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A plot of land is in the shape of a right triangle, with the longest side of length ${param3}${wordingUnit} and another side of length ${param1}${wordingUnit}. What is the perimeter of this plot of land?` :
                            `What is the perimeter of a plot of land shaped like a right triangle, if it has a leg length of ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A kite is in the shape of a right triangle, with leg lengths of ${param1}${wordingUnit} and ${param2}${wordingUnit}.  A metal wire runs along the outer edge of all sides of the triangle.  How long is the whole metal wire?` :
                            `You're crafting art with a piece of paper in the shape of a right triangle.  It has one leg of length ${param1}${wordingUnit} and a second leg length of ${param2}${wordingUnit}. What is the perimeter of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the perimeter of a right triangle whose legs are ${param1}${wordingUnit} and ${param2}${wordingUnit} long.`;
                        break;
                };
                // Get correct answer
                correctAnswerNumber = perimeter;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = correctAnswerNumber - Math.floor(10*Math.random()+1);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = param1*param2/2;
                if(param1*param2%2!==0){
                    wrong3Number = `${param1*param2}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }  
        },
        findArea: {
            numbers: function(difficulty) {
                // Get parameters
                let easyB = Math.floor(9*Math.random())+1; 
                let hardB = Math.floor(15*Math.random())+1;
                let paramB = (difficulty>1) ? hardB : easyB;
                let easyH = Math.floor(11*Math.random())+1;
                let hardH = Math.floor(15*Math.random())+1;
                let paramH = (difficulty>1) ? hardH : easyH;
                // Vary the specific wording
                let wordChoiceB = ["a base of", "base", "a base length of"];
                let wordingB = randomlySelect(wordChoiceB);
                let wordChoiceH = ["a height of", "height"];
                let wordingH = randomlySelect(wordChoiceH);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a triangle with ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a triangle has ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a triangle has one side of length ${paramB}${wordingUnit} and a height perpendicular to that side of ${paramH}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular plot of land has ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}. What is the area of this plot of land?` :
                            `What is the area of a triangular plot of land with ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular kite has ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}. What is the area of this kite?` :
                            `You're crafting art with a triangular piece of paper that has ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}. How much paper material is this, in square ${wordingUnit}?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a triangle with ${wordingB} ${paramB}${wordingUnit} and ${wordingH} ${paramH}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = Math.round(paramB*paramH/2);
                if(paramB*paramH%2!==0){
                    correctAnswerNumber = `${paramB*paramH}&frasl;2`;
                }
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = paramB*paramH;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = paramB*paramH + Math.floor(10*Math.random()) - 5;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = 2*paramB + 2*paramH;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            ofRightTriangle: function(difficulty){
                // Get parameters; only perfect Pythag triples
                let pythagTriples = [[3,4,5],[5,12,13],[6,8,10]];
                if(difficulty>1){
                    pythagTriples.push([9,12,15],[10,24,26],[11,60,61],[12,16,20],[15,20,25]);
                }
                let sides = randomlySelect(pythagTriples);
                let legs = (Math.random()>0.5) ? [sides[0],sides[1]] : [sides[1],sides[0]];
                let param1 = legs[0];
                let param2 = legs[1];
                let param3 = sides[2]; // hypotenuse
                // Vary the specific wording
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                let correctAnswerNumber = 0;
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the area of a right triangle with one leg of length ${param1}${wordingUnit} and a hypotenuse of ${param3}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a right triangle has one leg of length ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}, then what is its area?`
                        break;
                    case 3:
                        problemDataText = `If a right triangle has a leg of length ${param1}${wordingUnit} and a hypotenuse of ${param3}${wordingUnit}, then ${wordingCommand} its area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A plot of land is in the shape of a right triangle, with the longest side of length ${param3}${wordingUnit} and another side of length ${param1}${wordingUnit}. What is the area of this plot of land?` :
                            `What is the area of a plot of land shaped like a right triangle, if it has a leg length of ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A kite is in the shape of a right triangle, with a leg length of ${param1}${wordingUnit} and a longest side length of ${param3}${wordingUnit}.  What is the area of this shape?` :
                            `You're crafting art with a piece of paper in the shape of a right triangle.  It has one leg of length ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}. What is the area of this piece of paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a right triangle whose hypotenuse is ${param3}${wordingUnit} and that has a leg that is ${param1}${wordingUnit} long.`;
                        break;
                };
                // Get correct answer
                let area = param1*param2/2;
                let areaText;
                if(param1*param2%2===0){
                    areaText = area;
                } else{
                    areaText = `${param1*param2}&frasl;2`;
                }
                correctAnswerNumber = areaText;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = param1 + param2 + param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = correctAnswerNumber - Math.floor(10*Math.random()+1);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = param1*param3/2;
                if(param1*param3%2!==0){
                    wrong3Number = `${param1*param3}&frasl;2`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }      
        },
        findMissingDimension: {
            givenPerimeter: function(difficulty) {
                // Get parameters
                let correctAnswerNumber = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(15*Math.random())+1;
                let param2 = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(15*Math.random())+1;
                let param3 = getTriangle3rdSide(correctAnswerNumber,param2);
                let perimeter = correctAnswerNumber + param2 + param3;
                // Vary the specific wording
                let wordChoiceLength = ["with two side lengths of", "with two sides that are", "that has two sides of lengths"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the third side length of a triangle ${wordingLength} ${param2}${wordingUnit} and ${param3}${wordingUnit}, and whose perimeter is ${perimeter}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a triangle has a perimeter of ${perimeter}${wordingUnit} and two sides that are ${param2}${wordingUnit} and ${param3}${wordingUnit}, then how long is its third side?`
                        break;
                    case 3:
                        problemDataText = `If a triangle has sides of ${param2}${wordingUnit} and ${param3}${wordingUnit}, and a perimeter of ${perimeter}${wordingUnit}, then ${wordingCommand} the length of its third side.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular plot of land is completely surrounded by ${perimeter}${wordingUnit} of fencing all along the three sides of its border. Two of its sides are ${param2}${wordingUnit} and ${param3}${wordingUnit} long.  What is the length of the third side?` :
                            `How long is the third side of a triangular plot of land whose perimeter is ${perimeter}${wordingUnit} and ${wordingLength} ${param2}${wordingUnit} and ${param3}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular kite has one side length of ${param2}${wordingUnit} and another side length of ${param3}${wordingUnit}. The kite has a metal frame along its outer edge on all three sides with a total length of ${perimeter}${wordingUnit}. What is the length of the third side?` :
                            `You're crafting art with a triangular piece of paper that has two side lengths of ${param2}${wordingUnit} and ${param3}${wordingUnit}. You attached ${perimeter}${wordingUnit} of string all along the three edges of the paper around the whole shape.  How long is the length of the third side of the paper?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the length of the third side of a triangle ${wordingLength} ${param2}${wordingUnit} and ${param3}${wordingUnit}, and that has a perimeter of ${perimeter}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = perimeter + param2 + param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = 2*perimeter*param2/param3;
                if(2*perimeter*param2%param3!==0){
                    let fraction = reduce(2*perimeter*param2,param3);
                    wrong2Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = perimeter - param2 - param3 - Math.floor(3*Math.random());
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<1){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // Get parameters
                let otherDimension = (difficulty<2) ? Math.floor(11*Math.random())+1 : Math.floor(19*Math.random())+1;
                let areaMin = (otherDimension/4 > 1) ? Math.floor(otherDimension/4) : 1;
                let areaMax = (difficulty<2) ? Math.floor(otherDimension*5) : Math.floor(otherDimension*10);
                let area = Math.floor((areaMax-areaMin)*Math.random())+areaMin;
                // Vary the specific wording
                let wordChoiceB = ["a base of", "base", "a base length of"];
                let wordingB = randomlySelect(wordChoiceB);
                let wordChoiceH = ["a height of", "height"];
                let wordingH = randomlySelect(wordChoiceH);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the height of a triangle with ${wordingB} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a triangle has ${wordingH} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is the length of its base?`
                        break;
                    case 3:
                        problemDataText = `If a triangle has ${wordingB} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its height.`
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular plot of land has ${wordingB} ${otherDimension}${wordingUnit} and covers an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the height of this triangular plot?` :
                            `How long is the base of a triangular plot of land with ${wordingH} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A triangular kite has ${wordingB} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the height of this triangular shape?` :
                            `You're crafting art with a triangular piece of paper that has ${wordingH} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the base length of this piece of paper in ${wordingUnit}?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the height of a triangle with ${wordingB} ${otherDimension}${wordingUnit} and an area of ${area}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = 2*area/otherDimension; 
                if(2*area%otherDimension!==0){
                    let fraction = reduce(2*area,otherDimension);
                    correctAnswerNumber = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = area*otherDimension/2;
                if(area*otherDimension%2!==0){
                    let fraction = reduce(area*otherDimension,2);
                    wrong1Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = Math.round(2*area/otherDimension) + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = Math.round(2*area/otherDimension) - Math.floor(5*Math.random());
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1 || wrong2Number<1){
                    wrong2Number = Math.round(2*area/otherDimension) + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = area/otherDimension;
                if(area%otherDimension!==0){
                    let fraction = reduce(area,otherDimension);
                    wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = Math.round(2*area/otherDimension) + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            ofRightTriangle: function(difficulty){
                // Get parameters; only perfect Pythag triples
                let pythagTriples = [[3,4,5],[5,12,13],[6,8,10]];
                if(difficulty>1){
                    pythagTriples.push([9,12,15],[10,24,26],[11,60,61],[12,16,20],[15,20,25]);
                }
                let sides = randomlySelect(pythagTriples);
                let legs = (Math.random()>0.5) ? [sides[0],sides[1]] : [sides[1],sides[0]];
                let param1 = legs[0];
                let param2 = legs[1];
                let param3 = sides[2]; // hypotenuse
                // Vary the specific wording
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                let correctAnswerNumber = 0;
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the length of the hypotenuse of a right triangle with one leg of length ${param1}${wordingUnit} and another leg of length ${param2}${wordingUnit}?`;
                        correctAnswerNumber = param3;
                        break;
                    case 2:
                        problemDataText = `If a right triangle has one leg of length ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}, then how long is the other leg?`
                        correctAnswerNumber = param2;
                        break;
                    case 3:
                        problemDataText = `If a right triangle has legs of length ${param1}${wordingUnit} and ${param2}${wordingUnit}, then ${wordingCommand} the length of its hypotenuse.`
                        correctAnswerNumber = param3;
                        break;
                    case 4:
                        wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A plot of land is in the shape of a right triangle, with the longest side of length ${param3}${wordingUnit} and another side of length ${param1}${wordingUnit}. What is the length of the third side of this plot of land?` :
                            `What is the length of the third side of a plot of land shaped like a right triangle, if it has a leg length of ${param1}${wordingUnit} and a hypotenuse of length ${param3}${wordingUnit}?` 
                        correctAnswerNumber = param2;
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `A carpenter's tool is in the shape of a right triangle, with leg lengths of ${param1}${wordingUnit} and ${param2}${wordingUnit}.  How long is the third side of this triangular tool?` :
                            `You're crafting art with a piece of paper in the shape of a right triangle.  It has one leg of length ${param1}${wordingUnit} and a second leg length of ${param2}${wordingUnit}. How long is the third side?` 
                        correctAnswerNumber = param3;
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the length of the hypotenuse of a right triangle whose legs are ${param1}${wordingUnit} and ${param2}${wordingUnit} long.`;
                        correctAnswerNumber = param3;
                        break;
                };
                // Get correct answer
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = param1 + param2;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = correctAnswerNumber - Math.floor(2*Math.random()+1);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = param1*param2;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }   
        }
    },

    circle: {
        findCircumference: {
            numbers: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1; 
                let hardNumber = Math.floor(19*Math.random())+1;
                let parameter = (difficulty>1) ? hardNumber : easyNumber;
                // Vary the specific wording
                let wordChoiceR = ["a radius measurement of", "a radius of", "radius"];
                let wordingR = randomlySelect(wordChoiceR);
                let wordChoiceD = ["a diameter measurement of", "a diameter of", "diameter"];
                let wordingD = randomlySelect(wordChoiceD);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                let problemDataText = "";
                let correctAnswerText, wrong1Text, wrong2Text, wrong3Text;
                // Problem given radius...
                if(Math.random()>0.5){
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the circumference of a circle with ${wordingR} ${parameter}${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingR} ${parameter}${wordingUnit}, then what is its circumference?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingR} ${parameter}${wordingUnit}, then ${wordingCommand} its circumference.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land has a radius of ${parameter}${wordingUnit}. It is surrounded by a fence all along its border.  What is the length of this fence?` :
                                `What is the circumference of a circular plot of land that has ${wordingR} ${parameter}${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has a radius of ${parameter}${wordingUnit}. What is the circumference of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingR} ${parameter}${wordingUnit}. What is the circumference of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the circumference of a circle with ${wordingR} ${parameter}${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = 2*parameter; //without pi
                    if(correctAnswerNumber===1){
                        correctAnswerNumber = "";
                    }
                    correctAnswerText = `${correctAnswerNumber}&pi;${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = Math.pow(parameter,2); //without pi
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    if(wrong1Number===1){
                        wrong1Number = "";
                    }
                    wrong1Text = `${wrong1Number}&pi;${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = parameter; //without pi
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    if(wrong2Number===1){
                        wrong2Number = "";
                    }
                    wrong2Text = `${wrong2Number}&pi;${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(parameter/2,2); //without pi
                    if(parameter%2!==0){
                        let fraction = reduce(Math.pow(parameter,2),4);
                        wrong3Number = `(${fraction[0]}&frasl;${fraction[1]})`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    if(wrong3Number===1){
                        wrong3Number = "";
                    }
                    wrong3Text = `${wrong3Number}&pi;${wordingUnit}`;
                } 
                // Problem given diameter...
                else { 
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the circumference of a circle with ${wordingD} ${parameter}${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingD} ${parameter}${wordingUnit}, then what is its circumference?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingD} ${parameter}${wordingUnit}, then ${wordingCommand} its circumference.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land has a diameter of ${parameter}${wordingUnit}. It is surrounded by a fence all along its border.  What is the length of this fence?` :
                                `What is the circumference of a circular plot of land that has ${wordingD} ${parameter}${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has a diameter of ${parameter}${wordingUnit}. What is the circumference of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingD} ${parameter}${wordingUnit}. What is the circumference of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the circumference of a circle with ${wordingD} ${parameter}${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = parameter; // without pi
                    if(correctAnswerNumber===1){
                        correctAnswerNumber = "";
                    }
                    correctAnswerText = `${correctAnswerNumber}&pi;${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = Math.pow(parameter,2); //without pi
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    if(wrong1Number===1){
                        wrong1Number = "";
                    }
                    wrong1Text = `${wrong1Number}&pi;${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = parameter/2; //without pi
                    if(parameter%2!==0){
                        wrong2Number = `${parameter}&frasl;2`;
                    }
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    if(wrong2Number===1){
                        wrong2Number = "";
                    }
                    wrong2Text = `${wrong2Number}&pi;${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(parameter/2,2); //without pi
                    if(parameter%2!==0){
                        let fraction = reduce(Math.pow(parameter,2),4);
                        wrong3Number = `(${fraction[0]}&frasl;${fraction[1]})`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    if(wrong3Number===1){
                        wrong3Number = "";
                    }
                    wrong3Text = `${wrong3Number}&pi;${wordingUnit}`;
                }
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }    
        },
        findArea: {
            numbers: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1; 
                let hardNumber = Math.floor(19*Math.random())+1;
                let parameter = (difficulty>1) ? hardNumber : easyNumber;
                // Vary the specific wording
                let wordChoiceR = ["a radius measurement of", "a radius of", "radius"];
                let wordingR = randomlySelect(wordChoiceR);
                let wordChoiceD = ["a diameter measurement of", "a diameter of", "diameter"];
                let wordingD = randomlySelect(wordChoiceD);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                let problemDataText = "";
                let correctAnswerText, wrong1Text, wrong2Text, wrong3Text;
                // Problem given radius...
                if(Math.random()>0.5){
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the area of a circle with ${wordingR} ${parameter}${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingR} ${parameter}${wordingUnit}, then what is its area?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingR} ${parameter}${wordingUnit}, then ${wordingCommand} its area.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land has a radius of ${parameter}${wordingUnit}. What is its area?` :
                                `What is the area of a circular plot of land that has ${wordingR} ${parameter}${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has a radius of ${parameter}${wordingUnit}. What is the area of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingR} ${parameter}${wordingUnit}. What is the area of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a circle with ${wordingR} ${parameter}${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = Math.pow(parameter,2); //without pi
                    if(correctAnswerNumber===1){
                        correctAnswerNumber = "";
                    }
                    correctAnswerText = `${correctAnswerNumber}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 1
                    let wrong1Number = 2*parameter; //without pi
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    if(wrong1Number===1){
                        wrong1Number = "";
                    }
                    wrong1Text = `${wrong1Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 2
                    let wrong2Number = 2*Math.pow(parameter,2); //without pi
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    if(wrong2Number===1){
                        wrong2Number = "";
                    }
                    wrong2Text = `${wrong2Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(parameter,2)/2; //without pi
                    if(Math.pow(parameter,2)%2!==0){
                        wrong3Number = `(${Math.pow(parameter,2)}&frasl;2)`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    if(wrong3Number===1){
                        wrong3Number = "";
                    }
                    wrong3Text = `${wrong3Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                } 
                // Problem given diameter...
                else { 
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the area of a circle with ${wordingD} ${parameter}${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingD} ${parameter}${wordingUnit}, then what is its area?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingD} ${parameter}${wordingUnit}, then ${wordingCommand} its area.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land has a diameter of ${parameter}${wordingUnit}. What is its area?` :
                                `What is the area of a circular plot of land that has ${wordingD} ${parameter}${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has a diameter of ${parameter}${wordingUnit}. What is the area of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingD} ${parameter}${wordingUnit}. What is the area of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the area of a circle with ${wordingD} ${parameter}${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = Math.pow(parameter/2,2); // without pi
                    if(correctAnswerNumber===1){
                        correctAnswerNumber = "";
                    }
                    if(parameter%2!==0){
                        let fractionCorrect = reduce(Math.pow(parameter,2),4);
                        correctAnswerNumber =  `(${fractionCorrect[0]}&frasl;${fractionCorrect[1]})`; 
                    }
                    correctAnswerText = `${correctAnswerNumber}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 1
                    let wrong1Number = parameter; //without pi
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    if(wrong1Number===1){
                        wrong1Number = "";
                    }
                    wrong1Text = `${wrong1Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 2
                    let wrong2Number = Math.pow(parameter,2); //without pi
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        if(parameter%2!==0){
                            let fractionCorrect = reduce(Math.pow(parameter,2),4);
                            wrong2Number =  `(${fractionCorrect[0]}&frasl;${fractionCorrect[1]+1})`;
                        } else {
                            wrong2Number = correctAnswerNumber + 2;
                        }
                    }
                    if(wrong2Number===1){
                        wrong2Number = "";
                    }
                    wrong2Text = `${wrong2Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(parameter,2)/2; //without pi
                    if(Math.pow(parameter,2)%2!==0){
                        wrong3Number = `(${Math.pow(parameter,2)}&frasl;2)`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    if(wrong3Number===1){
                        wrong3Number = "";
                    }
                    wrong3Text = `${wrong3Number}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                }
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }    
        },
        findMissingDimension: {
            givenCircumference: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1;  // either radius or diameter
                let hardNumber = Math.floor(19*Math.random())+1;
                let parameter = 1; // circumference; to change later
                // Vary the specific wording
                let wordChoiceC = ["a circumference of", "circumference"];
                let wordingC = randomlySelect(wordChoiceC);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                let problemDataText = "";
                let correctAnswerText, wrong1Text, wrong2Text, wrong3Text;
                // Find the radius...
                if(Math.random()>0.5){
                    let radius = (difficulty<2) ? easyNumber : hardNumber;
                    parameter = 2*radius; // circumference without pi
                    if(parameter===1){
                        parameter = "";
                    }
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the radius of a circle with ${wordingC} ${parameter}&pi;${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingC} ${parameter}&pi;${wordingUnit}, then what is its radius?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingC} ${parameter}&pi;${wordingUnit}, then ${wordingCommand} its radius.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land is surrounded by ${parameter}&pi;${wordingUnit} of fencing all along its border.  What is the radius of this plot of land?` :
                                `What is the radius of a circular plot of land that has ${wordingC} ${parameter}&pi;${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has ${wordingC} ${parameter}&pi;${wordingUnit}. What is the radius of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingC} ${parameter}&pi;${wordingUnit}. What is the radius of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the radius of a circle with ${wordingC} ${parameter}&pi;${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = radius;
                    correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = 2*radius;
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    wrong1Text = `${wrong1Number}${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = radius - 1; 
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    wrong2Text = `${wrong2Number}${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(radius/2,2); //without pi
                    if(radius%2!==0){
                        let fraction = reduce(Math.pow(radius,2),4);
                        wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    wrong3Text = `${wrong3Number}${wordingUnit}`;
                } 
                // Problem given diameter...
                else { 
                    let diameter = (difficulty<2) ? easyNumber : hardNumber;
                    parameter = diameter; // circumference without pi
                    if(parameter===1){
                        parameter = "";
                    }
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the diameter of a circle with ${wordingC} ${parameter}&pi;${wordingUnit}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingC} ${parameter}&pi;${wordingUnit}, then what is its diameter?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingC} ${parameter}&pi;${wordingUnit}, then ${wordingCommand} its diameter.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land has ${parameter}&pi;${wordingUnit} of fencing all along its border.  What is the diameter of this plot of land?` :
                                `What is the diameter of a circular plot of land that has ${wordingC} ${parameter}&pi;${wordingUnit}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has ${wordingC} ${parameter}&pi;${wordingUnit}. What is the diameter of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingC} ${parameter}&pi;${wordingUnit}. What is the diameter of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the diameter of a circle with ${wordingC} ${parameter}&pi;${wordingUnit}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = diameter;
                    correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = Math.pow(diameter,2);
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    wrong1Text = `${wrong1Number}${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = diameter/2;
                    if(diameter%2!==0){
                        wrong2Number = `${diameter}&frasl;2`;
                    }
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    wrong2Text = `${wrong2Number}${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(diameter/2,2); //without pi
                    if(diameter%2!==0){
                        let fraction = reduce(Math.pow(diameter,2),4);
                        wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    wrong3Text = `${wrong3Number}${wordingUnit}`;
                }
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenArea: function(difficulty) {
                // Get parameters
                let easyNumber = Math.floor(11*Math.random())+1;  // either radius or diameter
                let hardNumber = Math.floor(19*Math.random())+1;
                let parameter = 1; // circumference; to change later
                // Vary the specific wording
                let wordChoiceA = ["an area of", "area"];
                let wordingA = randomlySelect(wordChoiceA);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                let problemDataText = "";
                let correctAnswerText, wrong1Text, wrong2Text, wrong3Text;
                // Find the radius...
                if(Math.random()>0.5){
                    let radius = (difficulty<2) ? easyNumber : hardNumber;
                    let area = Math.pow(radius,2); // area without pi
                    parameter = area; // area without pi shown in text
                    if(parameter===1){
                        parameter = "";
                    }
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the radius of a circle with ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its radius?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its radius.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land covers ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.  What is the radius of this plot of land?` :
                                `What is the radius of a circular plot of land that has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the radius of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the radius of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the radius of a circle with ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = radius;
                    correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = 2*radius;
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    wrong1Text = `${wrong1Number}${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = radius - 1; 
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number || wrong2Number<1){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    wrong2Text = `${wrong2Number}${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(radius/2,2); //without pi
                    if(radius%2!==0){
                        let fraction = reduce(Math.pow(radius,2),4);
                        wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    wrong3Text = `${wrong3Number}${wordingUnit}`;
                } 
                // Problem given diameter...
                else { 
                    let diameter = (difficulty<2) ? easyNumber : hardNumber;
                    let area = Math.pow(diameter/2,2); // area without pi
                    parameter = area; // area without pi displayed in text
                    if(diameter%2!==0){
                        let fraction = reduce(Math.pow(diameter,2),4);
                        parameter = `(${fraction[0]}&frasl;${fraction[1]})`;
                    }
                    if(parameter===1){
                        parameter = "";
                    }
                    // Vary the overall problem wording
                    let numOfOverallWordingCases = 6; // change this to match number of cases below;
                    let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                    switch(wordChoiceOverall){
                        case 1:
                            problemDataText = `What is the diameter of a circle with ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                            break;
                        case 2:
                            problemDataText = `If a circle has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its diameter?`
                            break;
                        case 3:
                            problemDataText = `If a circle has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its diameter.`
                            break;
                        case 4:
                            wordChoiceUnit = [" mi", " km", " yd", " ft", " m"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular plot of land covers ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.  What is the diameter of this plot of land?` :
                                `What is the diameter of a circular plot of land that has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                            break;
                        case 5:
                            wordChoiceUnit = [" in", " cm", " m", " ft"];
                            wordingUnit = randomlySelect(wordChoiceUnit);
                            problemDataText = (Math.random()>0.5) ? 
                                `A circular tabletop has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the diameter of the tabletop?` :
                                `You're crafting art with a circular piece of paper.  The paper has ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the diameter of this piece of paper?` 
                            break;
                        default:
                            problemDataText = `${capitalizeFirstLetter(wordingCommand)} the diameter of a circle with ${wordingA} ${parameter}&pi;${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                            break;
                    };
                    // Get correct answer
                    let correctAnswerNumber = diameter;
                    correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                    // Get wrong answer 1
                    let wrong1Number = Math.pow(diameter,2);
                    if(wrong1Number===correctAnswerNumber){
                        wrong1Number = correctAnswerNumber + 1;
                    }
                    wrong1Text = `${wrong1Number}${wordingUnit}`;
                    // Get wrong answer 2
                    let wrong2Number = diameter/2;
                    if(diameter%2!==0){
                        wrong2Number = `${diameter}&frasl;2`;
                    }
                    if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                        wrong2Number = correctAnswerNumber + 2;
                    }
                    wrong2Text = `${wrong2Number}${wordingUnit}`;
                    // Get wrong answer 3
                    let wrong3Number = Math.pow(diameter/2,2); //without pi
                    if(diameter%2!==0){
                        let fraction = reduce(Math.pow(diameter,2),4);
                        wrong3Number = `${fraction[0]}&frasl;${fraction[1]}`;
                    }
                    if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number){
                        wrong3Number = correctAnswerNumber + 3;
                    }
                    wrong3Text = `${wrong3Number}${wordingUnit}`;
                }
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }
        }
    },

    rectangularPrism: {
        findSurfaceArea: {
            numbers: function(difficulty) {
                // Get parameters
                let easyL = Math.floor(8*Math.random())+1; 
                let hardL = Math.floor(11*Math.random())+1;
                let paramL = (difficulty>1) ? hardL : easyL;
                let easyW = (easyL>4) ? Math.floor(4*Math.random())+1 : Math.floor(8*Math.random())+1;
                let hardW = Math.floor(11*Math.random())+1;
                let paramW = (difficulty>1) ? hardW : easyW;
                let easyH = (easyL>4 || easyW>4) ? Math.floor(4*Math.random())+1 : Math.floor(8*Math.random())+1;
                let hardH = Math.floor(11*Math.random())+1;
                let paramH = (difficulty>1) ? hardH : easyH;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the surface area of a rectangular prism with ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}, then what is its surface area?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}, then ${wordingCommand} its surface area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `An empty container in the shape of a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}. You want to cover the entire surface of the container in paper. How much paper do you need, in square ${wordingUnit}?` :
                        `What is the surface area of a container in the shape of a rectangular prism that is ${paramL}${wordingUnit} by ${paramW}${wordingUnit} by ${paramH}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism that is ${paramW}${wordingUnit} wide, ${paramL}${wordingUnit} long, and ${paramH}${wordingUnit} tall. What is the surface area of the aquarium?` :
                            `A box is shaped like a rectangular prism.  It is ${paramL}${wordingUnit} long, ${paramW}${wordingUnit} wide, and ${paramH}${wordingUnit} tall. What is the surface area of this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the surface area of a rectangular prism with ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = 2*paramL*paramW + 2*paramL*paramH + 2*paramW*paramH;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = paramL*paramW*paramH;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = paramL*paramW + paramL*paramH + paramW*paramH;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = 2*paramL*paramW + 2*paramL*paramH + 2*paramW*paramH +  Math.floor(20*Math.random()) - 10;;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<0){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenVolume: function(difficulty) {
                // Get parameters
                let param1 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param2 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param3 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let volume = param1*param2*param3;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the surface area of a rectangular prism with ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}, then what is its surface area?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}, then ${wordingCommand} its surface area.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `An empty container in the shape of a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}. You want to cover the entire surface of the container in paper. How much paper do you need, in square ${wordingUnit}?` :
                        `What is the surface area of a container in the shape of a rectangular prism that is ${param1}${wordingUnit} wide and ${param2}${wordingUnit} long, and that has a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism that is ${param1}${wordingUnit} wide, ${param2}${wordingUnit} long, and has a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}. What is the surface area of the aquarium?` :
                            `A box is shaped like a rectangular prism.  It is ${param1}${wordingUnit} tall and ${param2}${wordingUnit} wide, and has a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}. What is the surface area of this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the surface area of a rectangular prism with a volume of ${volume}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}, ${wordingLength} ${param1}${wordingUnit}, and ${wordingWidth} ${param2}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = 2*param1*param2 + 2*param2*param3 + 2*param1*param3;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = param1*param2 + param2*param3 + param1*param3;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = correctAnswerNumber - Math.floor(20*Math.random() + 1);
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<1){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }        
        },
        findVolume: {
            numbers: function(difficulty) {
                // Get parameters
                let easyL = Math.floor(11*Math.random())+1; 
                let hardL = Math.floor(11*Math.random())+1;
                let paramL = (difficulty>1) ? hardL : easyL;
                let easyW = (easyL>8) ? Math.floor(4*Math.random())+1 : Math.floor(11*Math.random())+1;
                let hardW = Math.floor(11*Math.random())+1;
                let paramW = (difficulty>1) ? hardW : easyW;
                let easyH = (easyL>8 || easyW>8) ? Math.floor(4*Math.random())+1 : Math.floor(11*Math.random())+1;
                let hardH = Math.floor(11*Math.random())+1;
                let paramH = (difficulty>1) ? hardH : easyH;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the volume of a rectangular prism with ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}, then what is its volume?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}, then ${wordingCommand} its volume.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `An empty room in the shape of a rectangular prism has ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}. How much air is inside this room, in cubic ${wordingUnit}?` :
                        `What is the volume of a room in the shape of a rectangular prism that is ${paramL}${wordingUnit} by ${paramW}${wordingUnit} by ${paramH}${wordingUnit}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism that is ${paramW}${wordingUnit} wide, ${paramL}${wordingUnit} long, and ${paramH}${wordingUnit} tall. How much water will this aquarium hold?` :
                            `A box is shaped like a rectangular prism.  It is ${paramL}${wordingUnit} long, ${paramW}${wordingUnit} wide, and ${paramH}${wordingUnit} tall. What is the volume of this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the volume of a rectangular prism with ${wordingLength} ${paramL}${wordingUnit}, ${wordingWidth} ${paramW}${wordingUnit}, and ${wordingHeight} ${paramH}${wordingUnit}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = paramL*paramW*paramH;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = 2*paramL*paramW + 2*paramL*paramH + 2*paramW*paramH;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = paramL*paramW + paramL*paramH + paramW*paramH;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = paramL*paramW*paramH +  Math.floor(20*Math.random()) - 10;;
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<0){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenSurfaceArea: function(difficulty) {
                // Get parameters
                let param1 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param2 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param3 = (difficulty<2) ? Math.floor(7*Math.random())+1 : Math.floor(11*Math.random())+1;
                let surfaceArea = 2*param1*param2 + 2*param2*param3 + 2*param1*param3;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the volume of a rectangular prism with ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its volume?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit},  ${wordingHeight} ${param2}${wordingUnit}, and a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its volume.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `An empty room in the shape of a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. How much air is inside this room, in cubic ${wordingUnit}?` :
                        `What is the volume of a room in the shape of a rectangular prism that is ${param1}${wordingUnit} long, ${param2}${wordingUnit} wide, and has a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism that is ${param1}${wordingUnit} wide, ${param2}${wordingUnit} tall, and has a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. How much water will this aquarium hold?` :
                            `A box is shaped like a rectangular prism.  It is ${param1}${wordingUnit} long, ${param2}${wordingUnit} wide, and has a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the volume of this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the volume of a rectangular prism with ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and a surface area of ${surfaceArea}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = param1*param2*param3;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 1
                let wrong1Number = param3;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 2
                let wrong2Number = 2*(param1 + param2 + param3);
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
                // Get wrong answer 3
                let wrong3Number = correctAnswerNumber - Math.floor(20*Math.random() + 1);
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<1){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            }    
        },
        findMissingDimension: {
            givenVolume: function(difficulty) {
                // Get parameters
                let param1 = (difficulty>1) ? Math.floor(11*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param2 = (difficulty>1) ? Math.floor(11*Math.random())+1 : Math.floor(11*Math.random())+1;
                let param3 = (difficulty>1) ? Math.floor(11*Math.random())+1 : Math.floor(11*Math.random())+1;
                let paramV = param1*param2*param3;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceVolume = ["a volume of", "volume"];
                let wordingVolume = randomlySelect(wordChoiceVolume);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the length of a rectangular prism with ${wordingHeight} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}, then what is its width?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}, then ${wordingCommand} its height.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `An empty room in the shape of a rectangular prism contains ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""} of air.  It has ${wordingWidth} ${param1}${wordingUnit} and ${wordingLength} ${param2}${wordingUnit}. How tall is the room?` :
                        `What is the width of a room in the shape of a rectangular prism that has ${wordingLength} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism and holds ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""} of water.  It is ${param1}${wordingUnit} wide and ${param2}${wordingUnit} long.  What is the height of the aquarium?` :
                            `A box is shaped like a rectangular prism.  It has ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}.  It is ${param1}${wordingUnit} long and ${param2}${wordingUnit} wide. How tall is this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the length of a rectangular prism with ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingVolume} ${paramV}${wordingUnit}${(wordingUnit!=="") ? "<sup>3</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = param3;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = paramV - param2 - param1;
                if(wrong1Number===correctAnswerNumber || wrong1Number<1){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = paramV*param1*param2;
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = param3 - Math.floor(10*Math.random());
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<1){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            },
            givenSurfaceArea: function(difficulty) {
                // Get parameters
                let easyL = Math.floor(8*Math.random())+1; 
                let hardL = Math.floor(11*Math.random())+1;
                let param1 = (difficulty>1) ? hardL : easyL;
                let easyW = Math.floor(8*Math.random())+1; 
                let hardW = Math.floor(11*Math.random())+1;
                let param2 = (difficulty>1) ? hardW : easyW;
                let easyH = Math.floor(11*Math.random())+1; 
                let hardH = Math.floor(14*Math.random())+1;
                let param3 = (difficulty>1) ? hardH : easyH;
                let paramSA = 2*param1*param2 + 2*param2*param3 + 2*param1*param3;
                // Vary the specific wording
                let wordChoiceLength = ["a length of", "length"];
                let wordingLength = randomlySelect(wordChoiceLength);
                let wordChoiceWidth = ["a width of", "width"];
                let wordingWidth = randomlySelect(wordChoiceWidth);
                let wordChoiceHeight = ["a height of", "height"];
                let wordingHeight = randomlySelect(wordChoiceHeight);
                let wordChoiceSA = ["a surface area of", "surface area"];
                let wordingSA = randomlySelect(wordChoiceSA);
                let wordChoiceUnit = ["", " in", " ft", " mi", " m", " cm", " km", " yd", " mm"];
                let wordingUnit = randomlySelect(wordChoiceUnit);
                let wordChoiceCommand = ["find", "calculate", "compute", "determine"];
                let wordingCommand = randomlySelect(wordChoiceCommand);
                // Vary the overall problem wording
                let numOfOverallWordingCases = 6; // change this to match number of cases below;
                let wordChoiceOverall = Math.floor(numOfOverallWordingCases*Math.random());
                let problemDataText = "";
                switch(wordChoiceOverall){
                    case 1:
                        problemDataText = `What is the length of a rectangular prism with ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?`;
                        break;
                    case 2:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then what is its width?`
                        break;
                    case 3:
                        problemDataText = `If a rectangular prism has ${wordingLength} ${param1}${wordingUnit}, ${wordingWidth} ${param2}${wordingUnit}, and ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}, then ${wordingCommand} its height.`
                        break;
                    case 4:
                        wordChoiceUnit = [" yd", " ft", " m"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                        `A container in the shape of a rectangular prism has ${wordingWidth} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the length of the container?` :
                        `What is the height of a container in the shape of a rectangular prism that is ${param1}${wordingUnit} long and ${param2}${wordingUnit} wide, if it has ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}?` 
                        break;
                    case 5:
                        wordChoiceUnit = [" in", " cm", " mm", " m", " ft"];
                        wordingUnit = randomlySelect(wordChoiceUnit);
                        problemDataText = (Math.random()>0.5) ? 
                            `An aquarium has the shape of a rectangular prism. It is ${param1}${wordingUnit} tall and ${param2}${wordingUnit} wide, with ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the length of the aquarium?` :
                            `A box is shaped like a rectangular prism.  It is ${param1}${wordingUnit} long, ${param2}${wordingUnit} wide, and has ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}. What is the height of this box?` 
                        break;
                    default:
                        problemDataText = `${capitalizeFirstLetter(wordingCommand)} the width of a rectangular prism with ${wordingLength} ${param1}${wordingUnit}, ${wordingHeight} ${param2}${wordingUnit}, and ${wordingSA} ${paramSA}${wordingUnit}${(wordingUnit!=="") ? "<sup>2</sup>" : ""}.`;
                        break;
                };
                // Get correct answer
                let correctAnswerNumber = param3;
                let correctAnswerText = `${correctAnswerNumber}${wordingUnit}`;
                // Get wrong answer 1
                let wrong1Number = 2*param1*param2 + 2*paramSA*param1 + 2*paramSA*param2;
                if(wrong1Number===correctAnswerNumber){
                    wrong1Number = correctAnswerNumber + 1;
                }
                let wrong1Text = `${wrong1Number}${wordingUnit}`;
                // Get wrong answer 2
                let wrong2Number = paramSA/(param1*param2);
                if(paramSA%(param1*param2)!==0){
                    let fraction = reduce(paramSA,(param1*param2));
                    wrong2Number = `${fraction[0]}&frasl;${fraction[1]}`;
                }
                if(wrong2Number===correctAnswerNumber || wrong2Number===wrong1Number){
                    wrong2Number = correctAnswerNumber + 2;
                }
                let wrong2Text = `${wrong2Number}${wordingUnit}`;
                // Get wrong answer 3
                let wrong3Number = param3 - Math.floor(6*Math.random());
                if(wrong3Number===correctAnswerNumber || wrong3Number===wrong1Number || wrong3Number===wrong2Number || wrong3Number<1){
                    wrong3Number = correctAnswerNumber + 3;
                }
                let wrong3Text = `${wrong3Number}${wordingUnit}`;
            
                return [problemDataText, correctAnswerText, wrong1Text, wrong2Text, wrong3Text];
            } 
        }
    }
    
};


